import { motion } from "framer-motion";
import { imgSrc, mergeContent } from "../lib/api";
import { useSite } from "../lib/SiteContext";

const EASE = [0.16, 1, 0.3, 1];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.9, ease: EASE, delay },
});

export default function Manifesto({ settings }) {
  const { settings: ctxSettings } = useSite();
  const s = settings || ctxSettings;
  const c = mergeContent(s);
  const bioParagraphs = (s?.bio || "").split(/\n+/).filter(Boolean);
  const portrait = s?.portrait_path || "";

  const chapters = [1, 2, 3]
    .map((i) => ({ n: `0${i}`, title: c[`chapter_${i}_title`], body: c[`chapter_${i}_body`] }))
    .filter((ch) => ch.title || ch.body);

  return (
    <section id="about" data-testid="about-section" className="scroll-mt-28 md:scroll-mt-0 border-t border-line bg-panel/20">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-36">
        <motion.div {...reveal()}>
          {c.about_eyebrow && (
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 rule-flare" />
              <p className="eyebrow !text-ochre/90">{c.about_eyebrow}</p>
            </div>
          )}
          <h2 className="font-display font-light text-4xl md:text-6xl leading-tight max-w-3xl" data-testid="about-artist-name">
            {s?.artist_name || "The Artist"}
          </h2>
        </motion.div>

        <div
          className={`mt-14 md:mt-20 grid gap-12 lg:gap-20 items-start ${
            portrait ? "lg:grid-cols-[0.85fr_1.15fr]" : "lg:grid-cols-1"
          }`}
        >
          {portrait && (
            <motion.div {...reveal(0.1)} className="relative w-fit mx-auto lg:mx-0">
              <div aria-hidden="true" className="absolute -bottom-5 -right-5 h-full w-full border border-ochre/30" />
              <img
                src={imgSrc(portrait)}
                alt="The artist"
                className="relative w-[min(440px,80vw)] aspect-[3/4] object-cover border border-line bg-panel"
                data-testid="about-portrait"
              />
            </motion.div>
          )}
          <div className={portrait ? "space-y-6" : "space-y-6 max-w-2xl"}>
            {bioParagraphs.length === 0 && (
              <p className="text-base leading-relaxed text-mute">
                The artist's story is coming soon.
              </p>
            )}
            {bioParagraphs.map((p, i) => (
              <motion.p
                key={i}
                {...reveal(0.1 + i * 0.08)}
                className={
                  i === 0
                    ? "font-display italic text-xl md:text-2xl leading-relaxed text-bone"
                    : "text-sm md:text-base leading-relaxed text-mute"
                }
                data-testid={i === 0 ? "about-bio-lead" : `about-bio-${i}`}
              >
                {p}
              </motion.p>
            ))}
          </div>
        </div>

        {chapters.length > 0 && (
          <div className="mt-20 md:mt-28 grid md:grid-cols-3 gap-10 md:gap-12 border-t border-line pt-14">
            {chapters.map((ch, i) => (
              <motion.div key={ch.n} {...reveal(i * 0.12)} data-testid={`manifesto-chapter-${ch.n}`}>
                <p className="font-meta text-sm text-ochre">{ch.n}</p>
                {ch.title && <h3 className="font-display text-2xl md:text-3xl mt-4">{ch.title}</h3>}
                {ch.body && <p className="text-sm leading-relaxed text-mute mt-4">{ch.body}</p>}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
